/**
 * @file ExtractedInformation.tsx
 *
 * @description Component for displaying extracted information in a clean, read-only grid layout.
 * Uses @rjsf/mui Form component in readonly mode with the eforceSchemaMapping or genericSchemaMapping.
 *
 * @module Components
 * @requires Material-UI
 * @requires React
 * @requires @rjsf/mui
 */

import React, { useMemo } from "react";
import { Box, Typography, Paper } from "@mui/material";
import type { JSONSchema7 } from "json-schema";
import { generateUiSchema } from "@/features/reports/utils/generateUiSchema";
import { FieldMapping } from "@/features/reports/config/fieldMapping";
import { SchemaForm } from "../../shared/ui";
import type {
  Citation,
  CitationGroup,
} from "../../features/narrative-citations/types";

interface ExtractedInformationProps {
  formData: any;
  jsonSchema: JSONSchema7;
  schemaMapping: Record<string, FieldMapping>;
  title?: string;
  narrativeEasterEggRefs?: React.MutableRefObject<{
    [narrativeID: string]: React.MutableRefObject<boolean>;
  }>;
  onEasterEggFound?: () => void;
  stationId?: string;
  onCite?: (group: CitationGroup) => void;
  canCite?: (citation: Citation) => boolean;
}

/**
 * Main component for displaying extracted information using RJSF Form in readonly mode
 */
export const ExtractedInformation: React.FC<ExtractedInformationProps> = ({
  formData,
  jsonSchema,
  schemaMapping,
  title,
  narrativeEasterEggRefs,
  onEasterEggFound,
  stationId,
  onCite,
  canCite,
}) => {
  // Generate UI schema based on the schema mapping
  const uiSchema = useMemo(() => {
    return generateUiSchema(jsonSchema, schemaMapping, jsonSchema);
  }, [jsonSchema, schemaMapping]);

  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        backgroundColor: "background.paper",
      }}
    >
      {title && (
        <Typography
          variant="h5"
          sx={{
            mb: 3,
            fontWeight: 600,
            color: "primary.main",
          }}
        >
          {title}
        </Typography>
      )}
      <Box
        sx={{
          width: "100%",
          // Consistent form field spacing
          "& .MuiFormControl-root": {
            mb: 2,
          },
          // Center boolean radio buttons
          "& .MuiFormControl-root:has(.MuiRadioGroup-root)": {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
          "& .MuiFormGroup-root.MuiRadioGroup-root": {
            justifyContent: "center",
          },
          // Ensure form fields don't overflow
          "& .MuiGrid-root": {
            maxWidth: "100%",
          },
          // Consistent input sizing
          "& .MuiOutlinedInput-root": {
            backgroundColor: "background.paper",
          },
          // Better field template spacing
          "& .field-object": {
            mb: 2,
          },
          "& .field-array": {
            mb: 3,
          },
          // Array item card styling
          "& .array-item": {
            p: 2,
            mb: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            backgroundColor: "background.paper",
          },
          // Better checkbox alignment
          "& .MuiFormControlLabel-root": {
            ml: 0,
          },
          // Grid layout improvements
          "& .MuiGrid-container": {
            alignItems: "flex-start",
          },
        }}
      >
        <SchemaForm
          schema={jsonSchema}
          formData={formData}
          uiSchema={uiSchema}
          readonly
          hideSubmit
          formContext={{
            narrativeEasterEggRefs,
            onEasterEggFound,
            stationId,
            onCite,
            canCite,
          }}
        />
      </Box>
    </Paper>
  );
};
